import { z } from "zod";
import type { Product } from "./product.js";

// ---------------------------------------------------------------------------
// 業種（D、#142 段4、docs/52 §5）。会社を作るとき1つ選ぶ（会員登録・新しい会社・Google／Apple）＝tenants.industry。
//   業種で入るもの＝分類（工種）と品目の見本（DB のトリガーが業種で入れる）・使う機能の既定（見積依頼は工務店だけ、軽減税率・税区分は食品・物販）。
//   食品 food（T2、../eigyouryoku/docs/14）は品目の見本に税区分つき（「商品（飲食料品）」だけ軽減）。
//   言葉の既定は製品で決まる（業種では変えない）。今ある会社は NULL（見本も使う機能も触らない）。
//   キーは DB の CHECK（tenants_industry_check）・fn_trades_seed・fn_items_seed と同じ
// ---------------------------------------------------------------------------

export const Industry = z.enum(["builder", "electric", "painting", "equipment", "wholesale", "design", "printing", "it", "events", "consulting", "food", "retail", "maintenance", "other"]);
export type Industry = z.infer<typeof Industry>;

/** 製品ごとに選べる業種（並びは画面の選択肢の順） */
export const INDUSTRIES: Record<Product, readonly Industry[]> = {
  // #289：卸売（建材・電材・設備機器・金物など、工務店や職人に売る会社。建材に限らない一般の卸の見本）
  genba: ["builder", "electric", "painting", "equipment", "wholesale"],
  eigyo: ["design", "printing", "it", "events", "consulting", "food", "retail", "maintenance", "other"],
};

export const INDUSTRY_LABEL: Record<Industry, string> = {
  builder: "工務店",
  electric: "電気",
  painting: "塗装",
  equipment: "設備",
  wholesale: "卸売",
  design: "制作・デザイン",
  printing: "印刷・出版",
  it: "IT・システム",
  events: "広告・イベント",
  consulting: "士業・コンサル",
  food: "食品（卸・製造・小売）",
  retail: "物販・卸",
  maintenance: "保守・サービス",
  other: "その他",
};

/** 選ぶ欄のはじめの値・業種を送らない API の呼び出し（今までの会員登録と同じ見本になる）。現場力＝工務店、営業力＝その他 */
export const DEFAULT_INDUSTRY: Record<Product, Industry> = { genba: "builder", eigyo: "other" };

/** その製品で選べる業種か（DB の CHECK と同じ） */
export const industryOk = (product: Product, industry: Industry): boolean => INDUSTRIES[product].includes(industry);

/** PUT /company/industry（管理者）。seed＝この業種の見本（分類・品目）も入れる（ログインの画面から初めて入った人の1枚。自社情報で変えるときは入れない） */
export const IndustryUpdate = z.object({ industry: Industry, seed: z.boolean().default(false) });
export type IndustryUpdate = z.infer<typeof IndustryUpdate>;

/** POST /trades/seed・/items/seed の答えの数（分類と品目の両方を入れたとき） */
export const IndustrySeedResult = z.object({ trades: z.number().int(), items: z.number().int(), message: z.string() });
export type IndustrySeedResult = z.infer<typeof IndustrySeedResult>;

export const INDUSTRY_MSG = {
  label: "業種",
  /** 設定の見本のボタン（Q7：4つ → 1つ） */
  seed: "業種見本追加",
  /** 業種が無い会社（今ある会社）の見本のボタンの横 */
  needIndustry: "業種を選ぶと、この業種の見本を入れられます",
  /** 自社情報で業種を変えたとき（10/6 射場さん） */
  changedNote: "見本は設定の『業種見本追加』で入れます",
  saved: "業種を「{name}」にしました",
  wrongProduct: "この製品では選べない業種です",
  /** ログインの画面から Google・Apple で初めて入った人の1枚 */
  welcomeTitle: "業種を選んでください",
  welcomeLead: "業種に合わせて、分類と品目の見本が入ります（あとで消せます）。",
  welcomeStart: "利用開始",
  required: "業種を選んでください",
} as const;
